/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Grid2,
  Chip,
  Typography,
} from '@mui/material';
import styles from './AllDay.module.css';
import { getSemenProcessingPreProcesss, SumationData } from './AllDayHelper';
import GradingUnavailable from '../Occyte-Grading/oocyteGradingUnavailable';
import EmbryoImageLightbox from './EmbryoImageLightbox';
import { getButtonStates } from '../../../utils/permissionService';

interface cycleProp {
  selectedCycleId: number;
}

const AllDay: React.FC<cycleProp> = ({ selectedCycleId }) => {
  const [dataAllDay, setDataAllDay] = useState<any[]>([]);
  const [pgtData, setPgtData] = useState<any[]>([]);
  const [summationData, setSummationData] = useState<SumationData>();
  const statusKeys = ['gv', 'mi', 'mii', 'fertilized', 'cleaved', 'blast', 'discarded', 'freezed', 'transferred'];

  const { t: tGeneral } = useTranslation('general');
  const { t: tCulture } = useTranslation('culture_config');
  const { t: tOocyteGrading } = useTranslation('oocyte_grading');

  const [lightboxData, setLightboxData] = useState<{
    imageUrl: string | null;
    embryo_no: number | null;
    label: string | null;
  }>({ imageUrl: null, embryo_no: null, label: null });

  const { canView } = getButtonStates('Lab', 'Embryo Culture', 'ALL', 'female');

  useEffect(() => {
    if (!canView) return;

    const fetchData = () => {
      getSemenProcessingPreProcesss(selectedCycleId)
        .then((response) => {
          const sortedResponse = response.all_days.sort(
            (a: any, b: any) => a.embryo_number - b.embryo_number
          );

          setDataAllDay(sortedResponse);
          const pgtInfo = sortedResponse.filter(
            (item: any) => item.has_pgt === true || item.has_pgt === false
          );
          setPgtData(pgtInfo);
          setSummationData(response.summation);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching data:', error);
        });
    };

    fetchData();
  }, [selectedCycleId, canView]);

  if (!canView) {
    return (
      <GradingUnavailable
        message={tGeneral('youDoNotHaveAccessToViewPage', {
          defaultValue: 'You do not have access to view this page.',
        })}
      />
    );
  }

  const daysWithData = [0, 1, 2, 3, 4, 5, 6, 7].filter((dayIndex) =>
    dataAllDay.some((row) => row.day === dayIndex && row.embryo_number !== null)
  );

  // Helper to calculate number of columns + image column
  const getDayColSpan = (dayIndex: number) => {
    if (dayIndex === 0) return 2;
    if (dayIndex === 1) return 3;
    if (dayIndex === 2) return 4;
    if (dayIndex === 3) return 3;
    if (dayIndex === 4) return 3;
    if (dayIndex >= 5 && dayIndex <= 7) return 4;
    return 1;
  };

  return (
    <>
      {dataAllDay.length > 0 ? (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={'space-between'}
            flexWrap="wrap"
            gap={2}
            padding={2}
          >
            {statusKeys.map((key, index) => (
              <Grid2
                key={index}
                container
                alignItems="center"
                spacing={3}
                size={{ xs: 6, sm: 4, md: 4, lg: 4 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textTransform: 'capitalize',
                    fontFamily: 'Nunito',
                    fontSize: '1rem',
                    fontWeight: 700,
                  }}
                >
                  {tGeneral(key, { defaultValue: key.toUpperCase() })}
                </Typography>
                {summationData && (
                  <Chip
                    label={summationData[key]}
                    sx={{
                      backgroundColor: '#fde8e8',
                      fontWeight: 'bold',
                      color: '#000',
                      borderRadius: '10px',
                    }}
                  />
                )}
                {index !== statusKeys.length - 1 && (
                  <Typography variant="body2">|</Typography>
                )}
              </Grid2>
            ))}
          </Box>

          {/* TABLE */}
          <div className={styles.tablesWrapper}>
            <TableContainer component={Paper} className={styles.tableContainer}>
              <div className={styles.tableScrollWrapper}>
                <Table>
                  {/* HEADER ROW 1 */}
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                        rowSpan={2}
                        className={`${styles.tableCell} ${styles.header}`}
                      >
                        {tGeneral('embryoNo', { defaultValue: 'Emb#' })}
                      </TableCell>

                      {daysWithData.map((dayIndex) => (
                        <TableCell
                          key={dayIndex}
                          align="center"
                          colSpan={getDayColSpan(dayIndex)}
                          className={`${styles.tableCell} ${styles.header}`}
                        >
                          {`${tGeneral('day', { defaultValue: 'Day' })} ${dayIndex}`}
                        </TableCell>
                      ))}
                      <TableCell
                        align="center"
                        rowSpan={2}
                        className={`${styles.tableCell} ${styles.header}`}
                      >
                        {tCulture('pgt', { defaultValue: 'PGT' })}
                      </TableCell>
                      <TableCell
                        align="center"
                        rowSpan={2}
                        className={`${styles.tableCell} ${styles.header}`}
                      >
                        {tGeneral('result', { defaultValue: 'Result' })}
                      </TableCell>
                      <TableCell
                        align="center"
                        rowSpan={2}
                        className={`${styles.tableCell} ${styles.header}`}
                      >
                        {tCulture('ah', { defaultValue: 'AH' })}
                      </TableCell>
                      <TableCell
                        align="center"
                        rowSpan={2}
                        className={`${styles.tableCell} ${styles.header}`}
                      >
                        {tGeneral('disposition', {
                          defaultValue: 'Disposition',
                        })}
                      </TableCell>
                    </TableRow>

                    {/* HEADER ROW 2 */}
                    <TableRow>
                      {daysWithData.map((dayIndex) => {
                        if (dayIndex === 0) {
                          return (
                            <>
                              <TableCell
                                key={`maturity_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tCulture('maturity', { defaultValue: 'Maturity' })}
                              </TableCell>

                              {/* NEW IMAGE COLUMN */}
                              <TableCell
                                key={`img_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('image', { defaultValue: 'Image' })}
                              </TableCell>
                            </>
                          );
                        }

                        if (dayIndex === 1) {
                          return (
                            <>
                              <TableCell
                                key={`pn_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tCulture('pn', { defaultValue: 'PN' })}
                              </TableCell>
                              <TableCell
                                key={`pb_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tCulture('pb', { defaultValue: 'PB' })}
                              </TableCell>

                              {/* NEW IMAGE COLUMN */}
                              <TableCell
                                key={`img_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('image')}
                              </TableCell>
                            </>
                          );
                        }

                        if (dayIndex === 2 || dayIndex === 3) {
                          return (
                            <>
                              <TableCell
                                key={`cell_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('numberOfCells', { defaultValue: '# Cells' })}
                              </TableCell>
                              <TableCell
                                key={`grade_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('grade', { defaultValue: 'Grade' })}
                              </TableCell>

                              {dayIndex === 2 && (
                                <TableCell
                                  key={`frag_${dayIndex}`}
                                  align="center"
                                  className={`${styles.tableCell} ${styles.header}`}
                                >
                                  {tGeneral('fragmentation', { defaultValue: '% Frag' })}
                                </TableCell>
                              )}

                              {/* NEW IMAGE COLUMN */}
                              <TableCell
                                key={`img_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('image')}
                              </TableCell>
                            </>
                          );
                        }

                        if (dayIndex >= 4 && dayIndex <= 7) {
                          return (
                            <>
                              <TableCell
                                key={`stage_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('stage', { defaultValue: 'Stage' })}
                              </TableCell>
                              <TableCell
                                key={`grade_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('grade', { defaultValue: 'Grade' })}
                              </TableCell>
                              {dayIndex >= 5 && (
                                <TableCell
                                  key={`ai_score_${dayIndex}`}
                                  align="center"
                                  className={`${styles.tableCell} ${styles.header}`}
                                >
                                  {tGeneral('aiScore', { defaultValue: 'AI Score' })}
                                </TableCell>
                              )}

                              {/* NEW IMAGE COLUMN */}
                              <TableCell
                                key={`img_${dayIndex}`}
                                align="center"
                                className={`${styles.tableCell} ${styles.header}`}
                              >
                                {tGeneral('image')}
                              </TableCell>
                            </>
                          );
                        }

                        return null;
                      })}
                    </TableRow>
                  </TableHead>

                  {/* BODY */}
                  <TableBody>
                    {dataAllDay
                      .filter((row) => row.embryo_number !== null)
                      .reduce((acc: any, curr: any) => {
                        const index = acc.findIndex(
                          (item: any) => item.embryo_number === curr.embryo_number
                        );
                        if (index === -1) {
                          acc.push({
                            embryo_number: curr.embryo_number,
                            embryo_no: curr.embryo_no,
                            days: { [curr.day]: curr },
                          });
                        } else {
                          acc[index].days[curr.day] = curr;
                          if (curr.embryo_no) acc[index].embryo_no = curr.embryo_no;
                        }
                        return acc;
                      }, [])
                      .map((row: any, index: number) => {
                        const pgtInfo =
                          pgtData.find(
                            (pgt: any) => pgt.embryo_number === row.embryo_number
                          ) || {};

                        return (
                          <TableRow key={index} className={styles.tableRow}>
                            <TableCell align="center" className={styles.tableCell}>
                              {row.embryo_no}
                            </TableCell>

                            {daysWithData.map((dayIndex) => {
                              const dayData = row.days[dayIndex] || {};
                              const dayImages = (dayData.files || []) as any[];

                              return (
                                <>
                                  {/* NORMAL CELLS FIRST */}
                                  {/* DAY 0 */}
                                  {dayIndex === 0 && (
                                    <>
                                      <TableCell
                                        align="center"
                                        className={styles.tableCell}
                                      >
                                        {dayData.oocyte_grade
                                          ? tOocyteGrading(
                                            dayData.oocyte_grade
                                              ?.replace(/\s+/g, '')
                                              .toLowerCase()
                                          )
                                          : '-'}
                                      </TableCell>
                                    </>
                                  )}

                                  {/* DAY 1 */}
                                  {dayIndex === 1 && (
                                    <>
                                      <TableCell
                                        align="center"
                                        className={styles.tableCell}
                                      >
                                        {dayData.pn_label || dayData.pn || '-'}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className={styles.tableCell}
                                      >
                                        {dayData.pb_label || dayData.pb || '-'}
                                      </TableCell>
                                    </>
                                  )}

                                  {/* DAY 2 & 3 */}
                                  {(dayIndex === 2 || dayIndex === 3) && (
                                    <>
                                      <TableCell
                                        align="center"
                                        className={styles.tableCell}
                                      >
                                        {dayData.number_of_cells_label ||
                                          dayData.number_of_cells ||
                                          '-'}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className={styles.tableCell}
                                      >
                                        {dayData.grade || '-'}
                                      </TableCell>

                                      {dayIndex === 2 && (
                                        <TableCell
                                          align="center"
                                          className={styles.tableCell}
                                        >
                                          {dayData.fragmentation_percentage_label ||
                                            dayData.fragmentation_percentage ||
                                            '-'}
                                        </TableCell>
                                      )}
                                    </>
                                  )}

                                  {/* DAY 4 – 7 */}
                                  {dayIndex >= 4 && dayIndex <= 7 && (
                                    <>
                                      <TableCell
                                        align="center"
                                        className={styles.tableCell}
                                      >
                                        {dayData.stage_label ||
                                          dayData.stage ||
                                          '-'}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className={styles.tableCell}
                                      >
                                        {dayData.grade || '-'}
                                      </TableCell>

                                      {dayIndex >= 5 && (
                                        <TableCell
                                          align="center"
                                          className={styles.tableCell}
                                        >
                                          {dayData.ai_score ?? '-'}
                                        </TableCell>
                                      )}
                                    </>
                                  )}

                                  {/* NEW IMAGE CELL */}
                                  <TableCell
                                    align="center"
                                    className={styles.tableCell}
                                  >
                                    {dayImages.length > 0 ? (
                                      <div
                                        className="d-flex flex-wrap justify-content-start"
                                        style={{ gap: '8px' }}
                                      >
                                        {dayImages.map((file: any, i: number) => (
                                          <img
                                            key={i}
                                            src={file.file}
                                            alt=""
                                            style={{
                                              cursor: 'pointer',
                                              border: '2px solid #ccc',
                                              borderRadius: '8px',
                                              padding: '1px',
                                              width: '45px',
                                              height: '45px',
                                              objectFit: 'cover',
                                            }}
                                            onClick={() =>
                                              setLightboxData({
                                                imageUrl: file.file!,
                                                embryo_no: row.embryo_no ?? row.embryo_number,
                                                label: `${tGeneral('embryo')} ${row.embryo_no ?? row.embryo_number} - ${tGeneral('day')} ${dayIndex}`,
                                              })
                                            }
                                          />
                                        ))}
                                      </div>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                </>
                              );
                            })}

                            {/* PGT */}
                            <TableCell align="center" className={styles.tableCell}>
                              {pgtInfo.has_pgt === false
                                ? tGeneral('no')
                                : pgtInfo.has_pgt === true
                                  ? tGeneral('yes')
                                  : '-'}
                            </TableCell>

                            {/* RESULT */}
                            <TableCell align="center" className={styles.tableCell}>
                              -
                            </TableCell>

                            {/* AH */}
                            <TableCell align="center" className={styles.tableCell}>
                              {pgtInfo.assisted_hatching === null
                                ? '-'
                                : pgtInfo.assisted_hatching
                                  ? tGeneral('yes')
                                  : tGeneral('no')}
                            </TableCell>

                            {/* DISPOSITION */}
                            <TableCell align="center" className={styles.tableCell}>
                              {(() => {
                                const disposition = Object.values(
                                  row.days as { [key: number]: any }
                                ).find((d: any) => d?.disposition)?.disposition;

                                if (disposition === 'FREEZE')
                                  return tOocyteGrading('freeze');
                                if (disposition === 'CULTURE')
                                  return tCulture('culture');
                                if (disposition === 'DISCARD')
                                  return tCulture('discard');
                                if (disposition === 'TRANSFER')
                                  return tCulture('transfer');

                                return disposition || '-';
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </div>

          {/* Lightbox */}
          {lightboxData.imageUrl && (
            <EmbryoImageLightbox
              imageUrl={lightboxData.imageUrl}
              embryoNumber={lightboxData.embryo_no}
              label={lightboxData.label}
              onClose={() =>
                setLightboxData({
                  imageUrl: null,
                  embryo_no: null,
                  label: null,
                })
              }
            />
          )}
        </>
      ) : (
        <GradingUnavailable
          message={tCulture('summaryUnavailable', {
            defaultValue: 'Summary unavailable. Please provide at least Day 0 data.',
          })}
        />
      )}
    </>
  );
};

export default AllDay;
